import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ComponentSync, LL_H_C, LL_V_L } from '@nu-art/thunderstorm-frontend/index';
import { Editor } from '@monaco-editor/react';
import { AntlrToMonacoAdapter, SimpleState } from './antlr-utils/TokenizerWrapper.js';
import { CharStreams } from 'antlr4ts';
import { splitExpression } from './utils.js';
import { _keys, filterDuplicates, filterInstances } from '@nu-art/ts-common';
import './Component_SmartExpressionEditor.scss';
import { Component_CalculatorButtons } from './calculator-buttons/Component_CalculatorButtons.js';
import { Module_ExpressionPrettierUtils } from '../../modules/expression-prettifier/Module_ExpressionPrettierUtils.js';
import { QuaiExpressionsV3Lexer } from '@app/hcs-shared/expression/grammar/QuaiExpressionsV3Lexer';
import { ModuleFE_Variable } from '../../../../_entity/variable/index.js';
import { ModuleFE_Expression } from '../../../../_entity/expression/index.js';
import { ModuleFE_Value } from '../../../../_entity/value/index.js';
import { ModuleFE_VariableRelation } from '../../../../_entity/variable-relation/index.js';
import { ExpressionType_GeneralFunction, ExpressionType_GeneralScore } from '@app/hcs-shared/expression';
export class Component_SmartExpressionEditor extends ComponentSync {
    langName = 'quai-expressions';
    //######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state = this.state || {};
        state.terms = nextProps.terms;
        state.value ??= nextProps.value;
        if (state.editor && (state.value !== nextProps.value)) {
            state.value = nextProps.value;
            state.editor.setValue(nextProps.value);
        }
        return state;
    }
    //######################### Initiate Monaco #########################
    MonacoSettings = {
        registerLanguage: () => {
            this.state.monaco.languages.register({ id: this.langName });
        },
        setTokenizers: () => {
            this.state.monaco.languages.setMonarchTokensProvider(this.langName, {
                tokenizer: {
                    root: [
                        [/./, { token: 'invalid' }],
                        [/T\d+/, { token: 'operator' }]
                    ]
                }
            });
            this.state.monaco.languages.setTokensProvider(this.langName, {
                getInitialState: () => new SimpleState(),
                tokenize: (line, state) => {
                    const lexer = new QuaiExpressionsV3Lexer(CharStreams.fromString(line));
                    const adapter = new AntlrToMonacoAdapter(lexer);
                    return {
                        endState: state,
                        tokens: adapter.tokenize(line)
                    };
                }
            });
        },
        defineAndSetTheme: () => {
            this.state.monaco.editor.defineTheme('quai-theme', {
                base: 'vs',
                inherit: true,
                rules: [
                    { token: 'number', foreground: '#1a4957' },
                    { token: 'string', foreground: '#c95132' },
                    { token: 'boolean', foreground: '#151594' },
                    { token: 'variable', foreground: '#800080' },
                    { token: 'identifier', foreground: '#94620a', fontStyle: 'italic' },
                    { token: 'whitespace', foreground: '#FFFFFF' },
                    { token: 'operator', foreground: '#17a666', fontStyle: 'bold', background: '#ff0000' },
                ],
                colors: {
                    'editor.background': '#FFFFFF',
                    'editor.foreground': '#3b3a3a',
                }
            });
            this.state.monaco.editor.setTheme('quai-theme');
        },
        setAutoCompleteProvider: () => {
            this.state.monaco.languages.registerCompletionItemProvider(this.langName, {
                provideCompletionItems: (model, position) => {
                    const currentWordInfo = model.getWordAtPosition(position);
                    const previousText = model.getValueInRange({
                        startLineNumber: position.lineNumber,
                        startColumn: 1,
                        endLineNumber: position.lineNumber,
                        endColumn: currentWordInfo ? currentWordInfo.startColumn : position.column
                    });
                    const regex = /(var|enum|attr|precipitating|relieving|expr)\(([^)]*?)(\).(\w*\.)*\w*)?$/;
                    const match = previousText.match(regex);
                    const prevWord = match ? match[0] : '';
                    let results = [];
                    if (prevWord.includes('expr')) {
                        results = filterInstances(this.autoCompleteFunctions.expression(currentWordInfo.word.trim()));
                    }
                    else if (prevWord.includes('enum')) {
                        results = this.autoCompleteFunctions.enumerated(currentWordInfo.word.trim());
                    }
                    else if (prevWord.includes('var')) {
                        results = this.autoCompleteFunctions.variable(currentWordInfo.word.trim());
                    }
                    else if (/var\([^)]*\)(\.\w*)*/.test(prevWord)) {
                        results = this.autoCompleteFunctions.property(prevWord.trim()) ?? [];
                    }
                    else if (prevWord.includes('attr')) {
                        if (match)
                            results = this.autoCompleteFunctions.attribute(match, currentWordInfo.word.trim());
                    }
                    else if (prevWord.includes('precipitating')) {
                        if (match)
                            results = this.autoCompleteFunctions.precipitatingOrRelieving(match, currentWordInfo.word.trim(), 'Precipitating Factor') ?? [];
                    }
                    else if (prevWord.includes('relieving')) {
                        if (match)
                            results = this.autoCompleteFunctions.precipitatingOrRelieving(match, currentWordInfo.word.trim(), 'Relieving Factor') ?? [];
                    }
                    const range = {
                        startLineNumber: position.lineNumber,
                        endLineNumber: position.lineNumber,
                        startColumn: currentWordInfo ? currentWordInfo.startColumn : position.column,
                        endColumn: position.column
                    };
                    return {
                        suggestions: filterDuplicates(results).map(result => ({
                            label: result,
                            kind: this.state.monaco.languages.CompletionItemKind.Text,
                            insertText: result,
                            insertTextRules: this.state.monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                            range
                        }))
                    };
                },
            });
        },
        setEditorValue: () => {
            const currExpr = this.state.value;
            this.state.editor.setValue(currExpr);
        },
        index: () => {
            this.MonacoSettings.registerLanguage();
            this.MonacoSettings.setTokenizers();
            this.MonacoSettings.defineAndSetTheme();
            this.MonacoSettings.setAutoCompleteProvider();
            this.MonacoSettings.setEditorValue();
        }
    };
    //######################### Monaco AutoComplete Functions #########################
    autoCompleteFunctions = {
        variable: (currWord) => {
            return filterDuplicates(ModuleFE_Variable.cache.filter(item => item._nameInLowercase.includes(currWord.toLowerCase() ?? '')).map(item => item.name));
        },
        expression: (currWord) => {
            return filterDuplicates(ModuleFE_Expression.cache.filter(item => item.label.toLowerCase()
                .includes(currWord.toLowerCase()) && (item.expressionType === ExpressionType_GeneralScore.dbKey || item.expressionType === ExpressionType_GeneralFunction.dbKey)))
                .map(item => item.label);
        },
        property: (prevWord) => {
            const varPattern = /\(([^)]+)\)/;
            const matchVar = varPattern.exec(prevWord)?.[1];
            const splitExpr = prevWord.match(/var\([^)]*\)\.(\w+(\.\w+)*)/);
            let propertiesArray = [];
            if (splitExpr && splitExpr[1]) {
                propertiesArray = splitExpr[1].split('.');
            }
            const variable = ModuleFE_Variable.cache.find(item => item._nameInLowercase === matchVar?.toLowerCase());
            const currValue = propertiesArray.reduce((value, currentProp) => {
                // @ts-ignore
                return value[currentProp];
            }, variable);
            if (!currValue)
                return;
            return _keys(currValue);
        },
        attribute: (match, currWord) => {
            if (match[2].length) {
                const baseVar = ModuleFE_Variable.cache.find(item => item._nameInLowercase === match[2].split(',')[0].toLowerCase());
                return filterDuplicates(ModuleFE_Variable.cache.filter(item => !!baseVar?._attributeVarIds?.includes(item._id)).map(item => item.name));
            }
            else {
                return filterDuplicates(ModuleFE_Variable.cache.filter(item => !!(item._nameInLowercase.includes(currWord) && item._attributeVarIds?.length))
                    .map(item => item.name));
            }
        },
        enumerated: (currWord) => {
            const values = this.resolveVariableEnumValues(splitExpression(this.state.editor.getValue()));
            if (values)
                return values;
            else
                return filterDuplicates(ModuleFE_Value.cache.filter(item => item.data?.value?.toLowerCase()
                    .includes(currWord.toLowerCase() ?? ''))).map(item => item.data.value);
        },
        precipitatingOrRelieving: (match, currWord, type) => {
            if (match[2].length) {
                const baseVar = ModuleFE_Variable.cache.find(item => item._nameInLowercase === match[2].split(',')[0].toLowerCase());
                if (baseVar)
                    return this.resolvePrecipitatingOrRelievingVars(baseVar, type);
            }
            else {
                return filterDuplicates(ModuleFE_Variable.cache.filter(item => !!(item._nameInLowercase.includes(currWord) && this.resolvePrecipitatingOrRelievingVars(item, type).length))
                    .map(item => item.name));
            }
        }
    };
    //######################### Logic #########################
    resolveVariableEnumValues = (splitExpr) => {
        const suspectedVar = splitExpr[splitExpr.length - 3];
        if (!suspectedVar?.includes('var'))
            return;
        const varName = Module_ExpressionPrettierUtils.findDependencies(suspectedVar, 'var')[0];
        const variable = ModuleFE_Variable.cache.find(item => item._nameInLowercase === varName.toLowerCase());
        if (!variable)
            return;
        const value = ModuleFE_Value.cache.unique(variable.valueId);
        return ModuleFE_Value.cache.filter(item => (value?.data).scope?.includes(item._id))
            .map(item => item.data.value);
    };
    resolvePrecipitatingOrRelievingVars = (baseVar, type) => {
        const relations = ModuleFE_VariableRelation.cache.filter(item => (item._firstVariableId === baseVar._id && item.relation === type));
        return filterDuplicates(filterInstances(relations.map(relation => ModuleFE_Variable.cache.unique(relation.rightSide[0]?.variableId)?.name)));
    };
    handleMonacoMount = (editor, monaco) => {
        this.setState({ monaco: monaco, editor: editor }, this.MonacoSettings.index);
    };
    //######################### Render #########################
    renderOperatorButtons = () => {
        if (this.props.hideButtons)
            return '';
        return _jsx(LL_H_C, { className: 'buttons', children: _jsx(Component_CalculatorButtons, { width: this.props.width ?? '100%', editor: this.state.editor, freeForm: this.props.freeForm }) });
    };
    render() {
        return _jsxs(LL_V_L, { className: 'smart-editor-container', children: [_jsx("div", { onBlur: (event) => {
                        this.props.onEditorBlur(this.state.editor.getValue());
                    }, children: _jsx(Editor, { onMount: this.handleMonacoMount, defaultLanguage: this.langName, height: this.props.height ?? '100%', width: this.props.width, options: {
                            overviewRulerBorder: false,
                            overviewRulerLanes: 0,
                            renderLineHighlight: this.props.withoutLineHighlight ? 'none' : 'line',
                            minimap: { enabled: false },
                            lineNumbers: 'off',
                            readOnly: !!this.props.disabled
                        } }) }), this.renderOperatorButtons()] });
    }
}
//# sourceMappingURL=Component_SmartExpressionEditor.js.map